/**
 * Formation chip-grid selector for the map toolbar.
 * Groups formations by defensive line count (3/4/5 at the back)
 * and renders each as a tappable pill for fast scanning.
 *
 * On desktop: rendered inside a Popover (managed by the Container).
 * On mobile: rendered inside the shared MobileSheetChrome bottom sheet
 * (managed by the Container, SE-5547).
 * @module features/squad-planning/components/FormationSelector
 */

import React, { memo } from "react";

import type {
  SquadPlanningFormation,
  FormationOption,
} from "@/features/squad-planning/types";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import FormationChip from "@/features/squad-planning/components/molecules/FormationChip";

/** Group labels keyed by the first digit of the formation label. */
const GROUP_LABELS: Readonly<Record<string, string>> = {
  "3": "3 at the back",
  "4": "4 at the back",
  "5": "5 at the back",
};

/** Group keys in display order. */
const GROUP_KEYS = ["3", "4", "5"] as const;

/** A group of formation options sharing the same back-line count. */
interface FormationGroup {
  readonly key: string;
  readonly label: string;
  readonly options: readonly FormationOption[];
}

/** Props for {@link FormationSelectorView}. */
export interface FormationSelectorViewProps {
  /** Currently selected formation enum value */
  readonly selectedFormation: SquadPlanningFormation;
  /** Formations grouped by back-line count */
  readonly groups: readonly FormationGroup[];
  /** Called when a formation chip is tapped */
  readonly onSelect: (value: string) => void;
}

/**
 * Pure presentation: grouped formation chip grid.
 * The wrapping (Popover on desktop, MobileSheetChrome on mobile) is handled
 * by the Container — this View only renders the chip content.
 * @param props - View props
 * @param props.selectedFormation - Active formation value for highlighting
 * @param props.groups - Grouped formation options
 * @param props.onSelect - Formation selection callback
 */
const FormationSelectorView = ({
  selectedFormation,
  groups,
  onSelect,
}: FormationSelectorViewProps) => (
  <Stack className="gap-5">
    {groups.map(group => (
      <Stack key={group.key} className="gap-2">
        <Text
          variant="micro-strong"
          className="uppercase tracking-wider text-content-subtle"
        >
          {group.label}
        </Text>
        <Stack direction="horizontal" className="flex-wrap gap-2">
          {group.options.map(opt => (
            <FormationChip
              key={opt.value}
              option={opt}
              isSelected={opt.value === selectedFormation}
              onSelect={onSelect}
            />
          ))}
        </Stack>
      </Stack>
    ))}
  </Stack>
);

FormationSelectorView.displayName = "FormationSelectorView";

export default memo(FormationSelectorView);

export { GROUP_LABELS, GROUP_KEYS };
export type { FormationGroup };
